import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePrevious from './../../hooks/usePrevious';

import AppContext from '../../contexts/AppContext';
import ConfigContext from '../../contexts/ConfigContext';
import DataContext from '../../contexts/DataContext';
import { Page } from '../enums';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_PAGE_COUNT = 9;
const ENPHASE_CUSTOMER_PAGE_COUNT = 7;

const pagesWithoutProgressBar = [
  Page.AddEVCharger,
  Page.TechnicalSupport,
  Page.UnableToService,
  Page.UpgradeMicroinverters,
];

export default function PageProgress() {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    totalPageCount,
    setTotalPageCount,
  } = useContext(AppContext);
  const { isDebrandedVersion } = useContext(ConfigContext);
  const { data } = useContext(DataContext);
  const { isEnphaseCustomer } = data;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const previousPageIndex = usePrevious(currentPageIndex);
  const [largestSeenPageIndex, setLargestSeenPageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    searchParams.get('page') as Page
  );
  const [clickedNextArrow, setClickedNextArrow] = useState(false);
  const showProgress = !pagesWithoutProgressBar.includes(currentPage);

  useEffect(() => {
    if (isEnphaseCustomer) {
      setTotalPageCount(ENPHASE_CUSTOMER_PAGE_COUNT);
    }

    if (!clickedNextArrow && previousPageIndex === currentPageIndex - 1) {
      setLargestSeenPageIndex(currentPageIndex);
    } else if (currentPageIndex > 0) {
      if (currentPageIndex > largestSeenPageIndex) {
        setLargestSeenPageIndex(currentPageIndex);
      }
    }
    setClickedNextArrow(false);
  }, [currentPageIndex]);

  useEffect(() => {
    setCurrentPage(searchParams.get('page') as Page);
  }, [searchParams]);

  useEffect(() => {
    if (currentPage === Page.EnlightenLogin) {
      setLargestSeenPageIndex(currentPageIndex);
    }
  }, [currentPage]);

  const percentage = ((currentPageIndex + 1) / totalPageCount) * 100;
  const isPrevButtonDisabled = currentPageIndex === 0;
  const isNextButtonDisabled = !(currentPageIndex + 1 <= largestSeenPageIndex);

  const goToPrevPage = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
      navigate(-1);
    }
  };

  const goToNextPage = () => {
    setClickedNextArrow(true);
    setCurrentPageIndex(currentPageIndex + 1);
    navigate(1);
  };

  return showProgress ? (
    <div className="relative flex justify-end items-center my-4 md:mt-0 md:mb-10">
      <div className="grow">
        <div className="bg-white md:bg-[#F4F3F0] h-2.5 rounded-full relative overflow-hidden">
          <div
            className={`${
              isDebrandedVersion ? 'bg-[#5ecf7f]' : 'bg-enph-orange'
            } h-full absolute left-0 top-0'`}
            style={{
              width: `${percentage || 0}%`,
              transition: 'width 0.5s',
            }}></div>
        </div>
      </div>
      <div className="flex">
        <button
          type="button"
          className="p-[1px] ml-4 md:ml-[30px] transition-opacity duration-300 disabled:opacity-100"
          disabled={isPrevButtonDisabled}
          onClick={goToPrevPage}
         // data-zuko-id={`${currentPage}_go_to_previous_slide`}
         >
          <IconArrow
            color={currentPageIndex > 0 ? '#3C3C3C' : '#dcdcd6'}
            direction="left"
          />
        </button>
        <button
          type="button"
          className="p-[1px] ml-2 transition-opacity duration-300 disabled:opacity-100"
          disabled={isNextButtonDisabled}
          onClick={goToNextPage}
         // data-zuko-id={`${currentPage}_go_to_next_slide`}
         >
          <IconArrow color={isNextButtonDisabled ? '#dcdcd6' : '#3C3C3C'} />
        </button>
      </div>
    </div>
  ) : null;
}

interface IconArrowProps {
  direction?: 'right' | 'left';
  color?: string;
}

function IconArrow({ color, direction }: IconArrowProps) {
  const widthClasses = 'w-[36px] h-[36px]';
  return (
    <div
      className={
        direction !== 'left' ? `rotate-180 ${widthClasses}` : widthClasses
      }>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="19" height="19" rx="9.5" />
        <path
          d="M11.5 7L8.5 10L11.5 13"
          stroke={color || 'black'}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect
          x="0.5"
          y="0.5"
          width="19"
          height="19"
          rx="9.5"
          stroke={color || 'black'}
        />
      </svg>
    </div>
  );
}
